/**
 * @description Zeigt die Nutzungsbedingungen der Anwendung an.
 */

import { useEffect } from "react";
import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  FileText,
  Users,
  AlertCircle,
  CheckCircle,
  ShieldBan,
  AlertTriangle,
  Scale,
  UserCheck,
  Copyright,
  FileWarning,
  RefreshCw,
  Database
} from "lucide-react";

interface TermsOfServiceProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

export function TermsOfService({
  navigate,
  currentUser,
  onLogout,
  onNavigateToSearchPage,
}: TermsOfServiceProps) {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const secondaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const warningPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 12%, var(--background))",
    borderLeftColor:
      "color-mix(in srgb, var(--accent) 70%, var(--background))",
  } as const;
  const warningIconStyle = {
    color: "color-mix(in srgb, var(--accent) 85%, var(--foreground))",
  } as const;
  const infoPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 12%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--primary) 35%, var(--background))",
  } as const;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-8">
            <h2 className="text-primary-heading mb-2">{t("terms.title")}</h2>
            <p className="text-secondary" style={secondaryTextStyle}>
              {t("terms.subtitle")}
            </p>
          </div>

          <div className="space-y-6">

            <div
              className="border-l-4 p-4 rounded-r-md shadow-sm"
              style={warningPanelStyle}
            >
              <div className="flex items-start">
                <AlertTriangle
                  className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0"
                  style={warningIconStyle}
                />
                <div>
                  <h3 className="text-sm font-semibold" style={primaryTextStyle}>
                     {t("legalNotice.projectDisclaimerTitle")}
                  </h3>
                  <p className="text-sm mt-1" style={secondaryTextStyle}>
                    {t("legalNotice.projectDisclaimerText")}
                  </p>
                </div>
              </div>
            </div>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <FileText className="w-5 h-5" style={iconStyle} />
                  {t("terms.scope")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.scopeText1")}</p>
                <p>{t("terms.scopeText2")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Users className="w-5 h-5" style={iconStyle} />
                  {t("terms.userGroups")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <div>
                  <h4 className="text-primary-heading font-semibold mb-2">
                    {t("terms.anonymousUsers")}
                  </h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>{t("terms.anonymousRight1")}</li>
                    <li>{t("terms.anonymousRight2")}</li>
                    <li>{t("terms.anonymousRight3")}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-primary-heading font-semibold mb-2">
                    {t("terms.registeredUsers")}
                  </h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>{t("terms.registeredRight1")}</li>
                    <li>{t("terms.registeredRight2")}</li>
                    <li>{t("terms.registeredRight3")}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <UserCheck className="w-5 h-5" style={iconStyle} />
                  {t("terms.registrationAccount")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.registrationIntro")}</p>

                <ul className="list-disc list-inside space-y-1 ml-4 font-medium" style={secondaryTextStyle}>
                  <li>{t("terms.regEmail")}</li>
                  <li>{t("terms.regUsername")}</li>
                  <li>{t("terms.regPassword")}</li>
                </ul>

                <p>{t("terms.registrationText1")}</p>
                <p>{t("terms.registrationText2")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <CheckCircle className="w-5 h-5" style={iconStyle} />
                  {t("terms.userObligations")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.userObligationsIntro")}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t("terms.obligation1")}</li>
                  <li>{t("terms.obligation2")}</li>
                  <li>{t("terms.obligation3")}</li>
                  <li>{t("terms.obligation4")}</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <ShieldBan className="w-5 h-5" style={iconStyle} />
                  {t("terms.terminationTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.terminationText")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Database className="w-5 h-5" style={iconStyle} />
                  {t("terms.dataQuality")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.dataQualityText1")}</p>
                <p>{t("terms.dataQualityText2")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <AlertCircle className="w-5 h-5" style={iconStyle} />
                  {t("terms.liability")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.liabilityText1")}</p>
                <p>{t("terms.liabilityText2")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Copyright className="w-5 h-5" style={iconStyle} />
                  {t("terms.copyright")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.copyrightText1")}</p>
                <p>{t("terms.copyrightText2")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <FileWarning className="w-5 h-5" style={iconStyle} />
                  {t("terms.copyrightClaims")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-body" style={primaryTextStyle}>
                <p>{t("terms.copyrightClaimsText")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <RefreshCw className="w-5 h-5" style={iconStyle} />
                  {t("terms.changes")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>{t("terms.changesText")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Scale className="w-5 h-5" style={iconStyle} />
                  {t("terms.jurisdiction")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>{t("terms.jurisdictionText")}</p>
              </CardContent>
            </Card>

            <div className="rounded-lg border p-6 text-sm text-body" style={{ ...infoPanelStyle, ...primaryTextStyle }}>
              <p className="mb-2">
                <strong>{t("terms.lastUpdated")}</strong>{" "}
                {language === "de" ? "Dezember 2025" : "December 2025"}
              </p>
              <p className="flex items-center gap-2">
                {t("terms.contactInfo")}
                <a
                  href={`mailto:${t("legalNotice.email")}`}
                  className="text-[var(--primary)] hover:underline font-medium"
                >
                  {t("legalNotice.email")}
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
